import React, { useEffect, useMemo, useState } from "react";
import { Sidebar } from "primereact/sidebar";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import { FiTrash2, FiRefreshCw } from "react-icons/fi";
import { TbEyeSearch, TbUserEdit } from "react-icons/tb";
import { RxCross2 } from "react-icons/rx";
import api from "../api/axios";
import "../assets/styles/page.css";

const PAGE_NAME = "Option Templates";

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const showRequestError = (error, fallbackMessage) => {
  // The Axios interceptor handles the global 403 popup.
  if (error.response?.status === 403) return;

  toast.error(
    error.response?.data?.message || fallbackMessage
  );
};

const emptyValue = () => ({
  label: "",
  value_key: "",
  price_adjustment: 0,
  is_default: false,
  sort_order: 0,
  status: "active",
});

const emptyField = () => ({
  field_name: "",
  field_key: "",
  field_type: "select",
  is_required: false,
  help_text: "",
  sort_order: 0,
  status: "active",
  values: [emptyValue()],
});

const initialForm = {
  template_name: "",
  template_key: "",
  status: "active",
  fields: [emptyField()],
};

const valueBasedTypes = ["select", "radio", "button", "checkbox"];

const createKey = (text = "") =>
  String(text)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const OptionTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const filteredTemplates = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) return templates;

    return templates.filter((template) => {
      return (
        template.template_name?.toLowerCase().includes(keyword) ||
        template.template_key?.toLowerCase().includes(keyword)
      );
    });
  }, [templates, search]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);

      const res = await api.get("/admin/products/option-templates", {
        headers: getPermissionHeaders("view"),
      });
      setTemplates(res.data?.data || []);
    } catch (error) {
      showRequestError(error, "Failed to fetch templates.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const resetForm = () => {
    setSelectedTemplate(null);
    setForm(initialForm);
  };

  const openCreate = () => {
    resetForm();
    setFormOpen(true);
  };

  const openEdit = async (template) => {
    try {
      const res = await api.get(
        `/admin/products/option-templates/${template.template_id}`,
        {
          headers: getPermissionHeaders("view"),
        }
      );

      const data = res.data?.data;

      setSelectedTemplate(data);

      setForm({
        template_name: data.template_name || "",
        template_key: data.template_key || "",
        status: data.status || "active",
        fields:
          data.fields?.length > 0
            ? data.fields.map((field) => ({
                field_name: field.field_name || "",
                field_key: field.field_key || "",
                field_type: field.field_type || "select",
                is_required: Boolean(field.is_required),
                help_text: field.help_text || "",
                sort_order: field.sort_order || 0,
                status: field.status || "active",
                values:
                  field.values?.length > 0
                    ? field.values.map((value) => ({
                        label: value.label || "",
                        value_key: value.value_key || "",
                        price_adjustment: value.price_adjustment || 0,
                        is_default: Boolean(value.is_default),
                        sort_order: value.sort_order || 0,
                        status: value.status || "active",
                      }))
                    : [emptyValue()],
              }))
            : [emptyField()],
      });

      setFormOpen(true);
    } catch (error) {
      showRequestError(error, "Failed to fetch template.");
    }
  };

  const openView = async (template) => {
    try {
      const res = await api.get(
        `/admin/products/option-templates/${template.template_id}`,
        {
          headers: getPermissionHeaders("view"),
        }
      );

      setSelectedTemplate(res.data?.data || template);
      setViewOpen(true);
    } catch (error) {
      showRequestError(error, "Failed to fetch template.");
      setSelectedTemplate(template);
      setViewOpen(true);
    }
  };

  const handleTemplateChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "template_name" && !prev.template_key
        ? { template_key: createKey(value) }
        : {}),
    }));
  };

  const addField = () => {
    setForm((prev) => ({
      ...prev,
      fields: [
        ...prev.fields,
        {
          ...emptyField(),
          sort_order: prev.fields.length,
        },
      ],
    }));
  };

  const updateField = (fieldIndex, key, value) => {
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.map((field, index) => {
        if (index !== fieldIndex) return field;

        const updated = {
          ...field,
          [key]: value,
          ...(key === "field_name" && !field.field_key
            ? { field_key: createKey(value) }
            : {}),
        };

        if (
          key === "field_type" &&
          !valueBasedTypes.includes(value) &&
          updated.values?.length
        ) {
          updated.values = [];
        }

        if (
          key === "field_type" &&
          valueBasedTypes.includes(value) &&
          (!updated.values || updated.values.length === 0)
        ) {
          updated.values = [emptyValue()];
        }

        return updated;
      }),
    }));
  };

  const removeField = (fieldIndex) => {
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.filter((_, index) => index !== fieldIndex),
    }));
  };

  const addValue = (fieldIndex) => {
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.map((field, index) =>
        index === fieldIndex
          ? {
              ...field,
              values: [
                ...(field.values || []),
                {
                  ...emptyValue(),
                  sort_order: field.values?.length || 0,
                },
              ],
            }
          : field
      ),
    }));
  };

  const updateValue = (fieldIndex, valueIndex, key, value) => {
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.map((field, index) => {
        if (index !== fieldIndex) return field;

        return {
          ...field,
          values: field.values.map((item, idx) => {
            if (idx !== valueIndex) return item;

            return {
              ...item,
              [key]: value,
              ...(key === "label" && !item.value_key
                ? { value_key: createKey(value) }
                : {}),
            };
          }),
        };
      }),
    }));
  };

  const removeValue = (fieldIndex, valueIndex) => {
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.map((field, index) =>
        index === fieldIndex
          ? {
              ...field,
              values: field.values.filter((_, idx) => idx !== valueIndex),
            }
          : field
      ),
    }));
  };

  const validateForm = () => {
    if (!form.template_name.trim()) {
      toast.error("Template name is required.");
      return false;
    }

    for (let i = 0; i < form.fields.length; i++) {
      const field = form.fields[i];

      if (!field.field_name.trim()) {
        toast.error(`Field ${i + 1}: Field name is required.`);
        return false;
      }

      if (valueBasedTypes.includes(field.field_type)) {
        const validValues = field.values?.filter((value) =>
          value.label?.trim()
        );

        if (!validValues?.length) {
          toast.error(`Field ${i + 1}: Add at least one value.`);
          return false;
        }
      }
    }

    return true;
  };

  const saveTemplate = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    try {
      setSaving(true);

      const payload = {
        template_name: form.template_name,
        template_key: form.template_key || createKey(form.template_name),
        status: form.status,
        fields: form.fields.map((field, fieldIndex) => ({
          field_name: field.field_name,
          field_key: field.field_key || createKey(field.field_name),
          field_type: field.field_type,
          is_required: Boolean(field.is_required),
          help_text: field.help_text || null,
          sort_order: fieldIndex,
          status: field.status || "active",
          values: valueBasedTypes.includes(field.field_type)
            ? (field.values || [])
                .filter((value) => value.label?.trim())
                .map((value, valueIndex) => ({
                  label: value.label,
                  value_key: value.value_key || createKey(value.label),
                  price_adjustment: Number(value.price_adjustment || 0),
                  is_default: Boolean(value.is_default),
                  sort_order: valueIndex,
                  status: value.status || "active",
                }))
            : [],
        })),
      };

      if (selectedTemplate?.template_id) {
        await api.put(
          `/admin/products/option-templates/${selectedTemplate.template_id}`,
          payload,
          {
            headers: getPermissionHeaders("edit"),
          }
        );

        toast.success("Template updated successfully.");
      } else {
        await api.post("/admin/products/option-templates", payload, {
          headers: getPermissionHeaders("add"),
        });
        toast.success("Template created successfully.");
      }

      setFormOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      showRequestError(error, "Template save failed.");
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = (template) => {
    confirmDialog({
      header: "Delete Option Template",
      message: `Delete "${template.template_name}"? This cannot be undone.`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await api.delete(
            `/admin/products/option-templates/${template.template_id}`,
            {
              headers: getPermissionHeaders("delete"),
            }
          );

          toast.success("Template deleted successfully.");
          fetchTemplates();
        } catch (error) {
          showRequestError(error, "Template delete failed.");
        }
      },
    });
  };

  return (
    <section className="admin-page option-template-page">
      <ConfirmDialog />

      <div className="page-header">
        <div>
          <p>Catalogue</p>
          <h1>Option Templates</h1>
          <span>
            Create reusable options like Logo, Hashtag, Gold Foil, Pieces and
            Sizes.
          </span>
        </div>

        <button type="button" className="primary-action" onClick={openCreate}>
          + Add Template
        </button>
      </div>

      <div className="product-filter-card">
        <div className="product-filter-top">
          <div>
            <h2>Templates</h2>
            <p>{loading ? "Loading templates..." : ""}</p>
          </div>

          <div className="filter-actions">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search template..."
            />

            <button
              type="button"
              className="ghost-action"
              onClick={fetchTemplates}
            >
              <FiRefreshCw />
            </button>
          </div>
        </div>
      </div>

      <div className="page-card">
        <div className="table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Template</th>
                <th>Key</th>
                <th>Fields</th>
                <th>Values</th>
                <th>Status</th>
                <th style={{ width: 120, textAlign: "center" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {!loading && filteredTemplates.length === 0 && (
                <tr>
                  <td colSpan="6" className="empty-cell">
                    No option templates found.
                  </td>
                </tr>
              )}

              {filteredTemplates.map((template) => {
                const fieldCount = template.fields?.length || 0;
                const valueCount =
                  template.fields?.reduce(
                    (total, field) => total + (field.values?.length || 0),
                    0
                  ) || 0;

                return (
                  <tr key={template.template_id}>
                    <td>
                      <strong>{template.template_name}</strong>
                    </td>

                    <td>{template.template_key}</td>
                    <td>{fieldCount}</td>
                    <td>{valueCount}</td>

                    <td>
                      <span className={`status-badge ${template.status}`}>
                        {template.status}
                      </span>
                    </td>

                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="ghost-action"
                          onClick={() => openView(template)}
                        >
                          <TbEyeSearch />
                        </button>

                        <button
                          type="button"
                          className="ghost-action"
                          onClick={() => openEdit(template)}
                        >
                          <TbUserEdit />
                        </button>

                        <button
                          type="button"
                          className="ghost-action"
                          onClick={() => deleteTemplate(template)}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Sidebar
        visible={formOpen}
        position="right"
        onHide={() => {
          setFormOpen(false);
          resetForm();
        }}
        className="premium-product-sidebar"
      >
        <form className="product-view-panel" onSubmit={saveTemplate}>
          <div className="product-view-title">
            <span>Option Template</span>
            <h2>
              {selectedTemplate?.template_id ? "Edit Template" : "Add Template"}
            </h2>
            <p>
              Build fields and values once, then attach this template to any
              product.
            </p>
          </div>

          <div className="productform-grid premium-sidebar-form">
            <label>
              Template Name
              <input
                value={form.template_name}
                onChange={(event) =>
                  handleTemplateChange("template_name", event.target.value)
                }
                placeholder="Logo"
                required
              />
            </label>

            <label>
              Template Key
              <input
                value={form.template_key}
                onChange={(event) =>
                  handleTemplateChange("template_key", event.target.value)
                }
                placeholder="logo"
              />
            </label>

            <label>
              Status
              <select
                value={form.status}
                onChange={(event) =>
                  handleTemplateChange("status", event.target.value)
                }
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
          </div>

          <div className="product-view-section premium-sidebar-form">
            <div className="section-heading">
              <div>
                <h3>Fields</h3>
                <p>Each field becomes a customer-facing option.</p>
              </div>

              <button type="button" className="secondary-action" onClick={addField}>
                + Add Field
              </button>
            </div>

            {form.fields.map((field, fieldIndex) => (
              <div className="nested-card option-card" key={fieldIndex} style={{ border: "0px", marginTop: "0px", paddingTop: "0px"}}>
                <div className="section-heading">
                  <strong>Field {fieldIndex + 1}</strong>

                  {form.fields.length > 1 && (
                    <button
                      type="button"
                      className="danger-action"
                      onClick={() => removeField(fieldIndex)}
                    >
                      <RxCross2 />
                    </button>
                  )}
                </div>

                <div className="productform-grid">
                  <label>
                    Field Name
                    <input
                      value={field.field_name}
                      onChange={(event) =>
                        updateField(fieldIndex, "field_name", event.target.value)
                      }
                      placeholder="Logo Type"
                      required
                    />
                  </label>

                  <label>
                    Field Key
                    <input
                      value={field.field_key}
                      onChange={(event) =>
                        updateField(fieldIndex, "field_key", event.target.value)
                      }
                      placeholder="logo_type"
                    />
                  </label>

                  <label>
                    Field Type
                    <select
                      value={field.field_type}
                      onChange={(event) =>
                        updateField(fieldIndex, "field_type", event.target.value)
                      }
                    >
                      <option value="select">Select</option>
                      <option value="radio">Radio</option>
                      <option value="button">Button</option>
                      <option value="checkbox">Checkbox</option>
                      <option value="text">Text</option>
                      <option value="textarea">Textarea</option>
                      <option value="file">File</option>
                      <option value="number">Number</option>
                    </select>
                  </label>

                  <label>
                    Status
                    <select
                      value={field.status}
                      onChange={(event) =>
                        updateField(fieldIndex, "status", event.target.value)
                      }
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </label>

                  <label>
                    Required
                    <div className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={Boolean(field.is_required)}
                        onChange={(event) =>
                          updateField(
                            fieldIndex,
                            "is_required",
                            event.target.checked
                          )
                        }
                        style={{ minHeight: "20px"}}
                      />
                      <span>Required field</span>
                    </div>
                  </label>

                  <label>
                    Help Text
                    <input
                      value={field.help_text}
                      onChange={(event) =>
                        updateField(fieldIndex, "help_text", event.target.value)
                      }
                      placeholder="Choose your logo option"
                    />
                  </label>
                </div>

                {valueBasedTypes.includes(field.field_type) && (
                  <div className="product-view-section" style={{ marginTop: "20px", border: "0", padding: "0"}}>
                    <div className="section-heading">
                      <div>
                        <h3>Values</h3>
                        <p>These are the selectable options.</p>
                      </div>

                      <button
                        type="button"
                        className="secondary-action"
                        onClick={() => addValue(fieldIndex)}
                      >
                        + Add Value
                      </button>
                    </div>

                    {(field.values || []).map((value, valueIndex) => (
                      <div
                        className="nested-card option-card"
                        key={`${fieldIndex}-${valueIndex}`}
                      >
                        <div className="productform-grid">
                          <label>
                            Label
                            <input
                              value={value.label}
                              onChange={(event) =>
                                updateValue(
                                  fieldIndex,
                                  valueIndex,
                                  "label",
                                  event.target.value
                                )
                              }
                              placeholder="Premium Logo"
                            />
                          </label>

                          <label>
                            Value Key
                            <input
                              value={value.value_key}
                              onChange={(event) =>
                                updateValue(
                                  fieldIndex,
                                  valueIndex,
                                  "value_key",
                                  event.target.value
                                )
                              }
                              placeholder="premium_logo"
                            />
                          </label>

                          <label>
                            Price Adjustment
                            <input
                              type="number"
                              value={value.price_adjustment}
                              onChange={(event) =>
                                updateValue(
                                  fieldIndex,
                                  valueIndex,
                                  "price_adjustment",
                                  event.target.value
                                )
                              }
                            />
                          </label>

                          <label>
                            Status
                            <select
                              value={value.status}
                              onChange={(event) =>
                                updateValue(
                                  fieldIndex,
                                  valueIndex,
                                  "status",
                                  event.target.value
                                )
                              }
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </label>

                          <label>
                           
                            <div className="checkbox-row" style={{ height: "50px"}}>
                              <input
                                type="checkbox"
                                checked={Boolean(value.is_default)}
                                onChange={(event) =>
                                  updateValue(
                                    fieldIndex,
                                    valueIndex,
                                    "is_default",
                                    event.target.checked
                                  )
                                }
                              />
                              <span>Default selected</span>
                            </div>
                          </label>

                          <button
                            type="button"
                            className="danger-action"
                            onClick={() => removeValue(fieldIndex, valueIndex)}
                            style={{ height: "50px"}}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="product-view-actions">
            <button
              type="button"
              className="secondary-action"
              onClick={() => {
                setFormOpen(false);
                resetForm();
              }}
            >
              Cancel
            </button>

            <button type="submit" className="primary-action" disabled={saving}>
              {saving ? "Saving..." : "Save Template"}
            </button>
          </div>
        </form>
      </Sidebar>

      <Sidebar
        visible={viewOpen}
        position="right"
        onHide={() => setViewOpen(false)}
        className="premium-product-sidebar"
      >
        {selectedTemplate && (
          <div className="product-view-panel">
            <div className="product-view-title">
              <span>{selectedTemplate.status}</span>
              <h2>{selectedTemplate.template_name}</h2>
              <p>{selectedTemplate.template_key}</p>
            </div>

            {selectedTemplate.fields?.length > 0 ? (
              selectedTemplate.fields.map((field) => (
                <div className="product-view-section" key={field.field_id}>
                  <h3>{field.field_name}</h3>

                  <div className="product-view-meta">
                    <div>
                      <strong>Key</strong>
                      <span>{field.field_key}</span>
                    </div>

                    <div>
                      <strong>Type</strong>
                      <span>{field.field_type}</span>
                    </div>

                    <div>
                      <strong>Required</strong>
                      <span>{field.is_required ? "Yes" : "No"}</span>
                    </div>

                    <div>
                      <strong>Status</strong>
                      <span>{field.status}</span>
                    </div>
                  </div>

                  {field.help_text && <p>{field.help_text}</p>}

                  {field.values?.length > 0 && (
                    <div className="product-view-options">
                      <strong>Values</strong>

                      <div>
                        {field.values.map((value) => (
                          <span key={value.value_id}>
                            {value.label}
                            {Number(value.price_adjustment || 0) > 0
                              ? ` +₹${Number(
                                  value.price_adjustment
                                ).toLocaleString()}`
                              : ""}
                            {value.is_default ? " • Default" : ""}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p>No fields added.</p>
            )}

            <div className="product-view-actions">
              <button
                type="button"
                className="secondary-action"
                onClick={() => setViewOpen(false)}
              >
                Close
              </button>

              <button
                type="button"
                className="primary-action"
                onClick={() => {
                  setViewOpen(false);
                  openEdit(selectedTemplate);
                }}
              >
                Edit Template
              </button>
            </div>
          </div>
        )}
      </Sidebar>
    </section>
  );
};

export default OptionTemplates;